import { useEffect, useMemo, useState, useRef } from 'react'
import { PropTypes } from 'prop-types'

import { Box, HStack, Stack, useToast, Divider, Wrap, Center, Link } from '@chakra-ui/react'
import { useAtom } from 'jotai'
import { breadcrumbAtom } from '@!/atoms/breadcrumbAtom'
import { alertMessageAtom, menuOpenAtom } from '@!/atoms/appSettingsAtom'
import { userHashAtom } from '@!/atoms/userInfoAtom'
import { safeSearchAtom } from '@!/atoms/safeSearchAtom'
import { HomeBanner, CoinPurchaseBanner, CampaignHomeBanner } from '@!/components/Banner'
import { SearchForm, TagSearchBox } from '@!/Store/Common/containers'
import { ScrollableNewBookList, ScrollableRankingList, ScrollableBulkPurchaseTitleList, TagItem, SafeSearchGuideModal } from '@!/Store/Home/components'
import { PrimaryButton } from '@!/components/Button'
import { getDevice } from '@!/Util/device'
import FeatureListWithBanners from '@!/Store/Home/components/FeatureListWithBanners'
import { TopTabs, ScrollableLatelyAccessList, SectionHeader } from '@!/Store/Common/components'
import { useTabs, useAppCookies, useTopFeatures, useTopNewBooks, useTopRankings, useTopBulkPurchaseTitles } from '@!/Hooks'
import { isBlank } from '@!/Util'

/* ストアのホーム画面
   現在はサイト全体のホーム
 */
export const HomePage = ({ home, tabName }) => {
  const toast = useToast()
  const [alertMessage, setAlertMessage] = useAtom(alertMessageAtom)
  const searchTags = home.search_tags || []
  const popularBooks = home.popular_books || []
  const [userHash] = useAtom(userHashAtom)
  const [safeSearch] = useAtom(safeSearchAtom)
  const isLoggedIn = useMemo(() => !!userHash, [userHash])
  const device = useMemo(() => getDevice(), [])
  const { currentTab } = useTabs(tabName)
  const genreName = currentTab.genreName
  const genreLabel = currentTab.genreLabel
  const [isSafeSearchModalOpen, setIsSafeSearchModalOpen] = useState(false)
  const { safeSearchModalConfirmed, setSafeSearchModalConfirmed } = useAppCookies()
  const safeSearchTagRef = useRef(null)
  const campaign = home.campaign_info
  const [, setBreadcrumb] = useAtom(breadcrumbAtom)

  const features = useTopFeatures(genreName)
  const newBooks = useTopNewBooks(genreName)
  const rankings = useTopRankings(genreName)
  const bulkPurchaseTitles = useTopBulkPurchaseTitles(genreName)

  useEffect(() => {
    if (tabName === 'home') return

    setBreadcrumb([{ name: 'マンガBANGブックス', ref: '/' }, { name: `${currentTab.label} マンガ一覧` }])
  }, [tabName, currentTab])

  useEffect(() => {
    if (!safeSearchModalConfirmed) {
      setIsSafeSearchModalOpen(true)
    }
  }, [safeSearchModalConfirmed])

  const handleSafeSearchModalClose = () => {
    setIsSafeSearchModalOpen(false)
    setSafeSearchModalConfirmed(true)
  }

  // 会員登録完了メッセージ
  useEffect(() => {
    if (alertMessage) {
      toast({
        title: 'Notification',
        description: alertMessage,
        status: 'info',
        duration: 5000,
        isClosable: true,
        onCloseComplete: () => {
          setAlertMessage(null)
        },
      })
    }
  }, [alertMessage])

  const [, setIsMenuOpen] = useAtom(menuOpenAtom)

  const handleSafeSearchClick = () => {
    setIsMenuOpen(true)
  }

  return (
    <>
      <TopTabs tabName={tabName} />
      <Box>
        <Box px={2} pt={4}>
          {campaign ? <CampaignHomeBanner campaign={campaign} tabName={tabName} /> : <HomeBanner tabName={tabName} />}
        </Box>
        <SearchForm tabName={tabName} placeholder={`${genreLabel}のマンガを検索`} />
        {popularBooks.length > 0 && (
          <HStack px={4} pb={4} fontSize="xs" justify="center" flexWrap="wrap">
            {popularBooks.map((book, index) => (
              <Link key={index} href={book.url}>
                {book.name}
              </Link>
            ))}
          </HStack>
        )}
        <Divider />
        <Wrap spacing={0} p={3} justify="center">
          <TagItem name="無料" whiteSpace="nowrap" as="a" href={genreName ? `/store/search/free/${genreName}` : '/store/search/free'} />
          {tabName === 'home' && <TagItem name="ジャンルから探す" whiteSpace="nowrap" as="a" href="/genres" />}
          <Box cursor="pointer" onClick={handleSafeSearchClick} ref={safeSearchTagRef} zIndex={1001}>
            <TagItem name={safeSearch ? 'セーフサーチ：ON' : 'セーフサーチ：OFF'} />
          </Box>
          <SafeSearchGuideModal isOpen={isSafeSearchModalOpen} onClose={handleSafeSearchModalClose} tagRef={safeSearchTagRef} />
        </Wrap>
        {!isLoggedIn ? (
          <HStack spacing={0} px={4}>
            <PrimaryButton variant="outline" as="a" href="/user/login" mr="11px">
              ログイン
            </PrimaryButton>
            <PrimaryButton as="a" href="/user/login#/registrations">
              簡単！無料会員登録
            </PrimaryButton>
          </HStack>
        ) : (
          <CoinPurchaseBanner device={device} />
        )}
        <Divider mt={3} />
        <Stack spacing={6} my={6}>
          <ScrollableLatelyAccessList tabName={tabName} />
          {!isBlank(rankings) && (
            <Stack spacing={4}>
              <SectionHeader text="デイリー人気ランキング" />
              <ScrollableRankingList rankings={rankings} tabName={tabName} />
            </Stack>
          )}
          {!isBlank(newBooks) && (
            <Stack spacing={4}>
              <SectionHeader text="毎日続々入荷！新刊をチェック" />
              <ScrollableNewBookList newBooks={newBooks} tabName={tabName} />
            </Stack>
          )}
          {!isBlank(bulkPurchaseTitles) && <ScrollableBulkPurchaseTitleList titles={bulkPurchaseTitles} tabName={tabName} />}
          <FeatureListWithBanners features={features} tabName={tabName} campaign={campaign} />
        </Stack>
        <Divider my={6} borderColor="bg.secondary" borderBottomWidth="12px" />
        <TagSearchBox searchTags={searchTags} genreTabName={tabName} />
        {tabName !== 'home' && (
          <Box pb={6}>
            <SearchForm tabName={tabName} placeholder={`${genreLabel}のマンガを検索`} />
            <Center>
              <Box cursor="pointer" onClick={handleSafeSearchClick}>
                <TagItem name={safeSearch ? 'セーフサーチ：ON' : 'セーフサーチ：OFF'} />
              </Box>
            </Center>
          </Box>
        )}
      </Box>
    </>
  )
}

HomePage.propTypes = {
  home: PropTypes.object.isRequired,
  tabName: PropTypes.string.isRequired,
}
